import type { Block, Briefing, GenEvent, GenerationPhase, MeDto, Outline, StudioToolKey } from '@ads/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { keys, useSession } from './queries';
import { streamGeneration } from './sse';

/* O estado de uma geração, montado a partir dos eventos do stream. */

export type GenStatus = 'idle' | 'running' | 'done' | 'failed';

export interface GenState {
  status: GenStatus;
  artifactId: string | null;
  title: string | null;
  sources: Array<{ key: string; label: string; coverage: number }>;
  phase: GenerationPhase | null;
  phaseLabel: string;
  phasesSeen: GenerationPhase[];
  outline: Outline | null;
  blocks: Block[];
  /** a partir de qual índice os blocos chegaram ao vivo (os anteriores vieram do replay) */
  liveFrom: number;
  tokens: number;
  error: string | null;
  startedAt: number | null;
  finishedAt: number | null;
}

const initial: GenState = {
  status: 'idle', artifactId: null, title: null, sources: [], phase: null, phaseLabel: '', phasesSeen: [],
  outline: null, blocks: [], liveFrom: 0, tokens: 0, error: null, startedAt: null, finishedAt: null,
};

export function useGeneration(tool: StudioToolKey) {
  const { projectId } = useSession();
  const qc = useQueryClient();
  const [s, setS] = useState<GenState>(initial);
  const abort = useRef<AbortController | null>(null);

  const apply = useCallback((e: GenEvent, replay: boolean) => {
    setS(prev => {
      switch (e.event) {
        case 'start': return { ...prev, status: 'running', artifactId: e.data.artifactId, title: e.data.title, startedAt: prev.startedAt ?? Date.now() };
        case 'context': return { ...prev, sources: e.data.sources };
        case 'phase': return { ...prev, phase: e.data.phase, phaseLabel: e.data.label, phasesSeen: prev.phasesSeen.includes(e.data.phase) ? prev.phasesSeen : [...prev.phasesSeen, e.data.phase] };
        case 'outline': return { ...prev, outline: e.data.outline, title: e.data.outline.title };
        case 'block': {
          if (e.data.index < prev.blocks.length) return prev;
          const blocks = [...prev.blocks, e.data.block];
          return { ...prev, blocks, liveFrom: replay ? blocks.length : prev.liveFrom };
        }
        case 'usage': return { ...prev, tokens: e.data.tokens };
        case 'done': return { ...prev, status: 'done', tokens: e.data.tokens || prev.tokens, phase: 'finishing', finishedAt: Date.now() };
        case 'error': return { ...prev, status: 'failed', error: e.data.message, finishedAt: Date.now() };
      }
      return prev;
    });
    if (e.event === 'done' && e.data.credits.quota) {
      qc.setQueryData<MeDto>(keys.me, me => (me ? { ...me, org: { ...me.org, credits: e.data.credits } } : me));
    }
    if (e.event === 'done' || e.event === 'error') {
      void qc.invalidateQueries({ queryKey: keys.artifacts(projectId) });
      void qc.invalidateQueries({ queryKey: keys.pulse(projectId) });
      if (e.event === 'error') void qc.invalidateQueries({ queryKey: keys.me });
    }
  }, [qc, projectId]);

  const run = useCallback(async (url: string, method: 'GET' | 'POST', body?: unknown, replayUntilLive = false) => {
    abort.current?.abort();
    const ctrl = new AbortController();
    abort.current = ctrl;
    const started = performance.now();
    try {
      await streamGeneration(url, {
        method, body, signal: ctrl.signal,
        /* numa reconexão, o que chega na primeira rajada é replay: entra sem animação */
        onEvent: e => apply(e, replayUntilLive && performance.now() - started < 250),
      });
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setS(prev => ({ ...prev, status: 'failed', error: err instanceof Error ? err.message : 'A conexão caiu.' }));
      void qc.invalidateQueries({ queryKey: keys.me });
    }
  }, [apply, qc]);

  const start = useCallback((briefing: Briefing, cost: number) => {
    setS({ ...initial, status: 'running', startedAt: Date.now(), phaseLabel: 'Preparando a bancada' });
    /* os tokens são reservados já no início: o medidor reflete isso na hora */
    qc.setQueryData<MeDto>(keys.me, me => (me ? { ...me, org: { ...me.org, credits: { ...me.org.credits, used: me.org.credits.used + cost } } } : me));
    void run(`/projects/${projectId}/artifacts/generate`, 'POST', { tool, briefing });
  }, [projectId, tool, run, qc]);

  const resume = useCallback((artifactId: string) => {
    setS({ ...initial, status: 'running', artifactId, phaseLabel: 'Reconectando' });
    void run(`/artifacts/${artifactId}/stream`, 'GET', undefined, true);
  }, [run]);

  const cancel = useCallback(async () => {
    if (s.artifactId) await api(`/artifacts/${s.artifactId}/cancel`, { method: 'POST' }).catch(() => {});
  }, [s.artifactId]);

  const reset = useCallback(() => { abort.current?.abort(); setS(initial); }, []);

  useEffect(() => () => abort.current?.abort(), []);
  return { state: s, start, resume, cancel, reset };
}
